import { EditorState, EditorStateConfig, Extension } from "@codemirror/state"
import { EditorView, ViewUpdate } from "@codemirror/view"
import { useEffect, useId, useLayoutEffect, useRef, useCallback } from "react"
import { useExtensions } from "./use-extensions"
import { useEvent } from "./use-event"
import { useLanguageExtensions } from "./use-language-extensions"
import type { CursorPosition } from "../code-editor"

type Props = {
  autoFocus?: boolean
  customExtensions?: Extension[]
  lineWrapping?: boolean
  onChange?: (value: string, viewUpdate: ViewUpdate) => void
  onCursorChange?: (position: CursorPosition) => void
  onEditorUpdate?: (update: ViewUpdate) => void
  onFormat?: (formattedCode: string) => void
  onSelectionChange?: (characterCount: number) => void
  readonly?: boolean
  value?: string
}

export function useCodeEditor(props: Props) {
  const { 
    value, 
    customExtensions, 
    autoFocus, 
    onChange, 
    readonly, 
    lineWrapping,
    onEditorUpdate, 
    onFormat, 
    onCursorChange,
    onSelectionChange,
  } = props

  const id = useId()

  const containerRef = useRef<HTMLDivElement>(null)
  const editorViewRef = useRef<EditorView | null>(null)
  const isInternalChangeRef = useRef(false)

  const getCursorPosition = useCallback((): CursorPosition | null => {
    const editor = editorViewRef.current
    if (!editor) return null

    const { state } = editor
    const pos = state.selection.main.head
    const lineInfo = state.doc.lineAt(pos)
    
    return {
      column: pos - lineInfo.from + 1,
      line: lineInfo.number,
    }
  }, [])

  const getSelectedCharacterCount = useCallback((): number => {
    const editor = editorViewRef.current
    if (!editor) return 0

    const { state } = editor
    const { from, to } = state.selection.main
    
    return to - from
  }, [])

  const extensions = useExtensions({
    readonly,
    lineWrapping,
    customExtensions,
    onChange: (newValue, update) => {
      isInternalChangeRef.current = true
      onChange?.(newValue, update)
    },
    onCursorChange,
    onEditorUpdate: onEditorUpdate ?? (() => {}),
    onFormat,
    onSelectionChange,
  })

  const { getLanguageExtensions } = useLanguageExtensions({
    id,
  })

  const { blurOnClickOutside } = useEvent(editorViewRef)

  const createEditor = useCallback(async () => {
    if (!containerRef.current) return

    // 加载完整的 TypeScript 语言功能
    const languageExtensions = await getLanguageExtensions(value ?? "")
    
    const config: EditorStateConfig = {
      doc: value ?? "",
      extensions: [...extensions, ...languageExtensions],
    }
    const parent = containerRef.current
    const state = EditorState.create(config)

    const editorView = new EditorView({
      parent,
      state,
    })

    editorViewRef.current = editorView
  }, [extensions, getLanguageExtensions, value])

  // init & deinit - 只在挂载时创建一次
  useLayoutEffect(() => {
    createEditor()

    return () => {
      if (editorViewRef.current) {
        editorViewRef.current.destroy()
        editorViewRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 当外部 value 改变时，更新编辑器内容
  useEffect(() => {
    const editor = editorViewRef.current
    if (!editor) return

    // 如果这是内部变化触发的更新，跳过
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false
      return
    }

    const currentValue = editor.state.doc.toString()
    const newValue = value ?? ""

    // 只在值真正不同时才更新
    if (currentValue !== newValue) {
      editor.dispatch({
        changes: {
          from: 0,
          to: currentValue.length,
          insert: newValue,
        },
      })
    }
  }, [value])

  useEffect(() => {
    if (autoFocus && editorViewRef.current) {
      editorViewRef.current.focus()
    }
  }, [autoFocus])

  useEffect(() => {
    document.addEventListener("click", blurOnClickOutside)

    return () => {
      document.removeEventListener("click", blurOnClickOutside)
    }
  }, [blurOnClickOutside])

  return {
    containerRef,
    editorViewRef,
    getCursorPosition,
    getSelectedCharacterCount,
  }
}
